import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import {
  ConfigurationPrintOptionDto2,
  SelectedPrintOptionDto,
} from '@/api-sdk';
import { baseStyles, buttonStyles } from '@/styles';

import { SummaryData } from '@/types';

@customElement('fg-configuration-print-option-summary')
@localized()
export class FgConfigurationPrintOptionSummary extends LitElement {
  @property({ type: Object })
  option?: ConfigurationPrintOptionDto2 | SelectedPrintOptionDto;

  reducePrintOptionData(
    option: ConfigurationPrintOptionDto2 | SelectedPrintOptionDto,
  ): SummaryData | undefined {
    if (
      'maxDimensions' in option &&
      'printMethod' in option &&
      'printPosition' in option
    ) {
      const { colorCount, image, maxDimensions, printMethod, printPosition } =
        option;

      return {
        colorCount: colorCount,
        image: image,
        dimensions: maxDimensions.description,
        printMethod: printMethod.name,
        printPosition: printPosition.name,
      };
    } else if (
      'maxSurface' in option &&
      'printTechnique' in option &&
      'position' in option
    ) {
      const { colorCount, image, maxSurface, printTechnique, position } =
        option;
      return {
        colorCount: colorCount,
        image: image,
        dimensions: maxSurface,
        printMethod: printTechnique,
        printPosition: position,
      };
    }
  }

  render() {
    if (!this.option) {
      return nothing;
    }

    const printOptionData = this.reducePrintOptionData(this.option);

    if (!printOptionData) {
      return nothing;
    }

    const {
      colorCount,
      image,
      dimensions: maxDimensions,
      printMethod: printMethodName,
      printPosition: printPositionName,
    } = printOptionData;

    return html`
      ${image
        ? html`
            <div class="image">
              <img
                src="${image}"
                alt="${printPositionName}"
                width="64"
                height="64"
              />
            </div>
          `
        : nothing}

      <div class="specs">
        <strong class="position">${printPositionName}</strong>

        <p class="print-technique-colors">
          ${printMethodName
            ? html`
                ${printMethodName}
              `
            : nothing}
          ${printMethodName && colorCount
            ? html`
                -
              `
            : nothing}
          ${colorCount
            ? t('content.configuration.colorCount', {
                count: colorCount,
              })
            : nothing}
        </p>

        ${maxDimensions
          ? html`
              <p class="max-surface">${maxDimensions}</p>
            `
          : nothing}

        <slot name="suffix"></slot>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      :host {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      img {
        background-color: #fff;
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        height: 64px;
        width: 64px;
        overflow: hidden;
        aspect-ratio: 1 / 1;
      }

      img {
        object-fit: contain;
        background-color: #fff;
      }

      .specs {
        display: flex;
        flex-direction: column;
      }

      .position {
        display: block;
        font-size: 1rem;
        margin-bottom: 5px;
      }

      .print-technique-colors {
        margin-bottom: 2px;
      }
    `,
  ];
}
